import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, IconButton, InputAdornment, type SxProps, TextField, Theme } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useDebounce } from '@hooks/useDebounce';

const sxSearchIcon = {
  color: 'text.disabled',
};

const sxSearchCircle = (theme: Theme) => ({
  color: theme.palette.grey[500],
});

type SearchProps = {
  setSearchResult: (data: string) => void;
  sx?: SxProps;
  defaultSearch?: string | null;
  isLoading?: boolean;
  debounce?: boolean;
};

export type IputSearchRef = { setValue: (value: string) => void, element: HTMLInputElement | null} | null

const Search = forwardRef<IputSearchRef, SearchProps>(
  ({ setSearchResult, sx = {}, defaultSearch, isLoading, debounce = true }, ref) => {
    const [value, setValue] = useState(defaultSearch || '');
    const debounceValue = useDebounce(value);
    const inputRef = useRef<HTMLInputElement>(null);

    const defaultRef = useRef(defaultSearch);

    if (defaultRef.current !== defaultSearch) {
      defaultRef.current = defaultSearch;
      setValue(defaultSearch || '');
    }

    useImperativeHandle(
      ref,
      () => {
        return {
          setValue,
          element: inputRef.current,
        };

      },
      [],
    );

    useEffect(() => {
      if (debounce) {
        setSearchResult(debounceValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceValue]);

    const handleClear = useCallback(() => {
      setValue('');
      setSearchResult('');
    }, [setSearchResult]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);
      if (!debounce) {
        setSearchResult(newValue);
      }
    };

    const endAdornment = useMemo(() => {
      const isDebouncing = debounce && debounceValue !== value;
      const showLoadingSpinner = isLoading || isDebouncing;

      if (showLoadingSpinner) {
        return (
          <InputAdornment position="end">
            <CircularProgress aria-label="Loading..." size={22} sx={sxSearchCircle} />
          </InputAdornment>
        );
      }

      if (value) {
        return (
          <InputAdornment position="end">
            <IconButton size="small" edge="end" onClick={handleClear} aria-label="clear search">
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        );
      }

      return null;
    }, [debounce, debounceValue, value, isLoading, handleClear]);

    return (
      <Box sx={{ ...sx }}>
        <TextField
          id="outlined-basic"
          placeholder="Search..."
          value={value}
          autoComplete="off"
          onChange={handleChange}
          inputRef={inputRef}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={sxSearchIcon} />
                </InputAdornment>
              ),
              endAdornment,
            },
          }}
          fullWidth
        />
      </Box>
    );
  },
);

export default Search;
